import { useNavigate } from "react-router-dom";
import { Trash2, Clock, Sparkles, Film } from "lucide-react";
import { mediaUrl, fmtTime } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const STATUS = {
  uploading: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  processing: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  analyzing: "text-[#3ad7ff] border-[#3ad7ff]/40 bg-[#3ad7ff]/10",
  uploaded: "text-slate-300 border-white/15 bg-white/5",
  ready: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  error: "text-rose-300 border-rose-400/30 bg-rose-400/10",
};

export const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  const s = STATUS[status] ? status : "uploaded";
  return <span data-testid="video-status-badge" className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border whitespace-nowrap ${STATUS[s]} ${s === "analyzing" ? "cx-scanbar" : ""}`}>{t(`st.${s}`)}</span>;
};

export const VideoCard = ({ v, onDelete, index = 0 }) => {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const clickable = !["uploading", "processing", "error"].includes(v.status);
  return (
    <div data-testid="dashboard-video-card" onClick={() => clickable && nav(`/editor/${v.id}`)} style={{ animationDelay: `${index * 0.05}s` }}
      className={`cx-card cx-rise rounded-xl overflow-hidden group ${clickable ? "cx-card-hover cursor-pointer" : "opacity-80"}`}>
      <div className="aspect-video bg-[#070912] relative overflow-hidden">
        {v.thumb_path ? (
          <img src={mediaUrl(`/videos/${v.id}/thumbnail`)} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-600"><Film className="w-10 h-10" /></div>
        )}
        <div className="absolute top-3 left-3"><StatusBadge status={v.status} /></div>
        {v.duration > 0 && <div className="absolute bottom-3 right-3 font-mono text-xs bg-black/70 px-2 py-0.5 rounded">{fmtTime(v.duration)}</div>}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display font-semibold text-white truncate" data-testid="video-card-title">{v.title}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{v.game || t("card.no_game")}</div>
          </div>
          <button data-testid="video-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(v); }} className="p-2 rounded-md text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#c79bff]" />{t("card.moments", { n: v.highlights.length })}</span>
          <span className="flex items-center gap-1.5"><Film className="w-3.5 h-3.5 text-[#3ad7ff]" />{t("card.clips", { n: v.clip_count })}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{new Date(v.created_at).toLocaleDateString(lang)}</span>
        </div>
        {v.error && <div className="text-xs text-rose-400 mt-2 line-clamp-2">{v.error}</div>}
      </div>
    </div>
  );
};
